/* eslint-disable max-lines-per-function */
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import ListHeader from "../../../components/tables/ListHeader";
import ListPagination from "../../../components/tables/ListPagination";
import { mongoTimeStampToString } from "../../../utils/orders";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ICredentialAudit } from "../../../interfaces/audits";
import { UserContext } from "../../../contexts/userContext";
import { IAlert } from "../../../interfaces/alert";
import { getAuditsList } from "../../../requests/auditsRequests";
import {
  DATE_FORMAT,
  DEFAULT_COUNTRY,
  ERROR,
  LOCALE,
  MARKETPLACE,
  MEDIUM,
  MERCHANT,
  ROWS_PER_PAGE,
  TAX_ID_LABELS,
} from "../../../utils/constants";
import GenericAlert from "../../../components/GenericAlert";
import Loader from "../../../components/Loader";
import { IPaginationParams } from "../../../interfaces/table";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";

const getStatusCSV = (matrix_processed: boolean, errorsAwsKey?: string) => {
  if (matrix_processed) return <Box sx={{ color: "green" }}>Procesado</Box>;
  if (errorsAwsKey)
    return <Box sx={{ color: "red" }}>Error en el procesamiento</Box>;
  return <Box sx={{ color: "orange" }}>No procesado</Box>;
};

const DEFAULT_COLUMNS = [
  { label: "Usuario" },
  { label: "Fecha de creación" },
  { label: "Fecha de modificación" },
  { label: "Estado de procesamiento de CSV" },
  { label: "Acciones" },
];

const handleDownload = (url: string) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = url.split("/").pop() || "matrix.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const validateDateFilters = (
  fromDate: DateTime | null,
  toDate: DateTime | null
) => {
  const todayEnd = DateTime.now().endOf("day");
  const isFuture = (d: DateTime) => d > todayEnd;
  const bothEmpty = !fromDate && !toDate;
  if (bothEmpty) {
    return {
      isValid: true,
      fromRequired: false,
      toRequired: false,
      rangeInvalid: false,
      fromFuture: false,
      toFuture: false,
    };
  }
  const oneFilled = (!!fromDate && !toDate) || (!fromDate && !!toDate);
  if (oneFilled) {
    return {
      isValid: false,
      fromRequired: !fromDate,
      toRequired: !toDate,
      rangeInvalid: false,
      fromFuture: !!fromDate && isFuture(fromDate),
      toFuture: !!toDate && isFuture(toDate),
    };
  }

  const fromFuture = !!fromDate && isFuture(fromDate);
  const toFuture = !!toDate && isFuture(toDate);
  if (fromFuture || toFuture) {
    return {
      isValid: false,
      fromRequired: false,
      toRequired: false,
      rangeInvalid: false,
      fromFuture,
      toFuture,
    };
  }

  const rangeInvalid = !!fromDate && !!toDate && toDate < fromDate;
  return {
    isValid: !rangeInvalid,
    fromRequired: false,
    toRequired: false,
    rangeInvalid,
    fromFuture: false,
    toFuture: false,
  };
};

type FiltersState = {
  merchant_cuit: string;
  fromDate: DateTime | null;
  toDate: DateTime | null;
};
const buildQuery = (
  selectedMarketplace: string,
  filters: FiltersState,
  page: number
) => {
  const params = new URLSearchParams();
  if (selectedMarketplace)
    params.set("marketplaceId", String(selectedMarketplace));

  params.set("limit", String(ROWS_PER_PAGE));
  params.set("skip", String(page * ROWS_PER_PAGE));

  if (filters.merchant_cuit.trim())
    params.set("merchant_cuit", filters.merchant_cuit.trim());

  if (filters.fromDate && filters.toDate) {
    params.set("fromDate", filters.fromDate.toISO() || "");
    params.set("toDate", filters.toDate.toISO() || "");
  }

  return `?${params.toString()}`;
};

const MatrixAudits = () => {
  const navigate = useNavigate();
  const { selectedMarketplace, currentUser } = useContext(UserContext);
  const [alert, setAlert] = useState<IAlert>({} as IAlert);
  const [loading, setLoading] = useState<boolean>(true);
  const [matrixAudits, setMatrixAudits] = useState<ICredentialAudit[]>([]);
  const isMarketplaceUser = currentUser.entity === MARKETPLACE;
  const isMerchantUser = currentUser.entity === MERCHANT;
  const [filters, setFilters] = useState<FiltersState>({
    merchant_cuit: "",
    fromDate: null,
    toDate: null,
  });
  const country = currentUser?.entityInfo?.country || DEFAULT_COUNTRY;
  const [errors, setErrors] = useState({
    fromRequired: false,
    toRequired: false,
    rangeInvalid: false,
    fromFuture: false,
    toFuture: false,
  });

  const [pagination, setPagination] = useState({
    count: 0,
    page: 0,
  } as IPaginationParams);

  const MARKETPLACE_COLUMNS = [
    {
      label: TAX_ID_LABELS[country],
    },
    {
      label: "Merchant",
    },
    ...DEFAULT_COLUMNS,
  ];

  const getAudits = async (newPage = 0, f: FiltersState = filters) => {
    setLoading(true);
    try {
      const query = buildQuery(selectedMarketplace, f, newPage);
      const { count, audits } = await getAuditsList(query);
      setMatrixAudits(audits);
      setPagination({ count, page: newPage });
      // Limpiar posibles alertas previas en caso de éxito, incluso si no hay resultados
      setAlert({} as IAlert);
    } catch (error) {
      setAlert({
        typeOfAlert: ERROR,
        message: "Hubo un error al obtener las matrices.",
        showAlert: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setAlert({} as IAlert);
    const {
      isValid,
      fromRequired,
      toRequired,
      rangeInvalid,
      fromFuture,
      toFuture,
    } = validateDateFilters(filters.fromDate, filters.toDate);

    const nextErrors = {
      fromRequired,
      toRequired,
      rangeInvalid,
      fromFuture,
      toFuture,
    };
    setErrors(nextErrors);

    if (!isValid || nextErrors.fromFuture || nextErrors.toFuture) return;

    getAudits();
  };

  const handleClear = () => {
    const cleared = {
      merchant_cuit: "",
      fromDate: null,
      toDate: null,
    } as FiltersState;
    setAlert({} as IAlert);
    // Limpiar errores asociados a los filtros
    setErrors({
      fromRequired: false,
      toRequired: false,
      rangeInvalid: false,
      fromFuture: false,
      toFuture: false,
    });
    setFilters(cleared);
    getAudits(0, cleared);
  };

  const handlePageChange = (newPage: number) => {
    setAlert({} as IAlert);
    getAudits(newPage);
  };

  useEffect(() => {
    getAudits();
  }, []);

  if (loading) return <Loader size={MEDIUM} />;

  return (
    <>
      {alert.showAlert && <GenericAlert alert={alert} />}

      <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={LOCALE}>
        <Box
          className="m-t-20"
          sx={{
            display: "flex",
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {isMarketplaceUser && (
              <TextField
                placeholder={`Buscar por ${TAX_ID_LABELS[country]}`}
                size="small"
                value={filters.merchant_cuit}
                onChange={(e) =>
                  setFilters((s) => ({ ...s, merchant_cuit: e.target.value }))
                }
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                fullWidth
                sx={{ width: { xs: "100%", sm: 300 } }}
              />
            )}
            <DatePicker<DateTime>
              label="Desde"
              value={filters.fromDate}
              onChange={(newValue) => {
                // Permitir limpiar el campo (null) y validar fechas válidas
                if (newValue === null) {
                  setFilters((s) => ({ ...s, fromDate: null }));
                  setErrors((e) => ({
                    ...e,
                    fromRequired: false,
                    rangeInvalid: false,
                    fromFuture: false,
                  }));
                  return;
                }
                if (!newValue.isValid) return;

                setFilters((s) => ({
                  ...s,
                  fromDate: newValue,
                  toDate: s.toDate && s.toDate < newValue ? null : s.toDate,
                }));

                setErrors((e) => ({
                  ...e,
                  fromRequired: false,
                  rangeInvalid: false,
                  fromFuture: false,
                }));
              }}
              inputFormat={DATE_FORMAT}
              disableFuture
              maxDate={filters.toDate ?? undefined}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  sx={{ width: { xs: "100%", sm: 220 } }}
                  error={
                    errors.fromRequired ||
                    errors.rangeInvalid ||
                    errors.fromFuture
                  }
                  helperText={
                    (errors.fromRequired && "Fecha desde requerida") ||
                    (errors.fromFuture && "No puede ser futura") ||
                    (errors.rangeInvalid &&
                      "Hasta debe ser igual o posterior a Desde") ||
                    params.helperText
                  }
                />
              )}
            />
            <DatePicker<DateTime>
              label="Hasta"
              value={filters.toDate}
              onChange={(newValue) => {
                if (newValue === null) {
                  setFilters((s) => ({ ...s, toDate: null }));
                  setErrors((e) => ({
                    ...e,
                    toRequired: false,
                    rangeInvalid: false,
                    toFuture: false,
                  }));
                  return;
                }
                if (!newValue.isValid) return;
                setFilters((s) => ({ ...s, toDate: newValue }));
                setErrors((e) => ({
                  ...e,
                  toRequired: false,
                  rangeInvalid: false,
                }));
              }}
              disableFuture
              inputFormat={DATE_FORMAT}
              minDate={filters.fromDate ?? undefined}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  sx={{ width: { xs: "100%", sm: 220 } }}
                  error={
                    errors.toRequired || errors.rangeInvalid || errors.toFuture
                  }
                  helperText={
                    (errors.toRequired && "Fecha hasta requerida") ||
                    (errors.toFuture && "No puede ser futura") ||
                    (errors.rangeInvalid &&
                      "Hasta debe ser igual o posterior a Desde") ||
                    params.helperText
                  }
                />
              )}
            />
          </Box>
          <Box sx={{ flex: 1, display: { xs: "none", sm: "block" } }} />
          <Button
            variant="contained"
            onClick={handleSearch}
            sx={{ width: { xs: "100%", sm: 200 }, height: 40 }}
          >
            Buscar
          </Button>
          <Button
            variant="outlined"
            onClick={handleClear}
            sx={{ width: { xs: "100%", sm: 120 }, height: 40 }}
          >
            Limpiar filtros
          </Button>
        </Box>
      </LocalizationProvider>

      <TableContainer
        component={Paper}
        className="m-t-40 br-20 bg-light"
        sx={{ overflowX: "auto" }}
      >
        <Table>
          <ListHeader
            columns={isMarketplaceUser ? MARKETPLACE_COLUMNS : DEFAULT_COLUMNS}
          />
          <TableBody>
            {matrixAudits.length > 0 ? (
              matrixAudits.map((audit) => {
                const downloadMatrixUrl = audit.awsKey;
                const downloadErrorsUrl = audit.errorsAwsKey;
                return (
                  <TableRow key={audit.id}>
                    {currentUser.entity === MARKETPLACE && (
                      <TableCell>{audit.merchant_cuit}</TableCell>
                    )}
                    {currentUser.entity === MARKETPLACE && (
                      <TableCell>{audit.merchant_name}</TableCell>
                    )}
                    <TableCell>{audit.user_name}</TableCell>
                    <TableCell>
                      {mongoTimeStampToString(audit.createdAt)}
                    </TableCell>
                    <TableCell>
                      {mongoTimeStampToString(audit.updatedAt)}
                    </TableCell>
                    <TableCell>
                      {getStatusCSV(audit.matrix_processed, audit.errorsAwsKey)}
                    </TableCell>
                    <TableCell>
                      {downloadMatrixUrl && (
                        <Tooltip title="Descargar matriz">
                          <IconButton
                            color="primary"
                            aria-label="Descargar matriz"
                            onClick={() => handleDownload(downloadMatrixUrl)}
                          >
                            <span className="material-symbols-rounded">
                              download
                            </span>
                          </IconButton>
                        </Tooltip>
                      )}
                      {downloadErrorsUrl && (
                        <Tooltip title="Descargar errores">
                          <IconButton
                            color="primary"
                            aria-label="Descargar errores"
                            onClick={() => handleDownload(downloadErrorsUrl)}
                          >
                            <span className="material-symbols-rounded">
                              error
                            </span>
                          </IconButton>
                        </Tooltip>
                      )}
                      {isMerchantUser && audit.matrix_id && (
                        <Tooltip title="Ver cobertura">
                          <IconButton
                            color="primary"
                            aria-label="Ver cobertura"
                            disabled={!audit.matrix_processed}
                            onClick={() =>
                              navigate(
                                `cobertura/${audit.matrix_id}?credential_id=${audit.credential_id}`
                              )
                            }
                          >
                            <span className="material-symbols-rounded">
                              pin_drop
                            </span>
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={isMarketplaceUser ? 6 : 5}>
                  No hay matrices para mostrar.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <ListPagination
          total={pagination.count}
          page={pagination.page}
          onChangePage={handlePageChange}
        />
      </TableContainer>
    </>
  );
};

export default MatrixAudits;
